import Fetchers from 'core/fetchers';
import Posters from 'shared/helpers/ProductHelper/posters';
import ApplicationController from './application_controller';


const DEFAULT_LIMIT = 4;
const DISCLAIMER_MODES = {
  STANDARD: 'standard',
  BUNDLED: 'bundled',
  DISABLED: 'disabled',
};

export default class CrossSellController extends ApplicationController {
  static targets = ['itemsContainer', 'title', 'discountMessage', 'disclaimer', 'module'];

  static values = {
    targetedItemClass: String,
    canvases: Array,
    dataSources: Array,
    limit: Number,
    disclaimerMode: String,
  }

  connect() {
    // If this controller is attached to a parent container that holds one or more
    // cross-sell module configs (data-cross-sell-target="module"), fetch for each
    // config and append resulting modules into this container.
    // Otherwise, maintain legacy behavior: treat this.element as a single module.
    if (this.hasModuleTarget && this.moduleTargets.length > 0) {
      this.fetchAllModules();
    } else {
      this.fetchCrossSell();
    }
  }

  disconnect() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }

  // Container mode: fetch suggestions for each module config under this container
  fetchAllModules() {
    const items = this.getItemsInfo();

    if (!items || items.length === 0) return;

    // Prepare module configs with ids
    const modules = this.moduleTargets.map((moduleConfigEl) => {
      const config = this.readModuleConfig(moduleConfigEl);
      config.moduleId = this.generateModuleId();
      return { moduleConfigEl, config };
    });

    // Sort by provided index if present; fallback to DOM order (stable sort)
    modules.sort((a, b) => {
      const ai = (a.config.index == null ? Number.POSITIVE_INFINITY : a.config.index);
      const bi = (b.config.index == null ? Number.POSITIVE_INFINITY : b.config.index);
      return ai - bi;
    });

    // Create placeholders in final order, then fire requests in parallel
    this.modulePlaceholders = new Map();
    modules.forEach(({ config }) => {
      const placeholder = document.createElement('div');
      placeholder.className = 'cross-sell-module m-cart__cross-sell-module';
      placeholder.setAttribute('data-cross-sell-placeholder-id', config.moduleId);
      this.element.appendChild(placeholder);
      this.modulePlaceholders.set(config.moduleId, placeholder);
    });

    modules.forEach(({ config }) => {
      this.fetchCrossSellForConfig(items, config);
    });
  }

  readModuleConfig(moduleConfigEl) {
    return {
      canvases: this.readArray(moduleConfigEl.dataset.crossSellCanvasesValue),
      data_sources: this.readArray(moduleConfigEl.dataset.crossSellDataSourcesValue),
      limit: this.readNumber(moduleConfigEl.dataset.crossSellLimitValue) || DEFAULT_LIMIT,
      index: this.readNumber(moduleConfigEl.dataset.crossSellIndexValue),
      disclaimer_mode: this.readString(moduleConfigEl.dataset.crossSellDisclaimerModeValue) || DISCLAIMER_MODES.STANDARD,
    };
  }

  readArray(raw) {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }

  readNumber(raw) {
    if (raw == null) return undefined;
    const num = Number(raw);
    return Number.isFinite(num) ? num : undefined;
  }

  readString(raw) {
    if (raw == null) return undefined;
    return String(raw);
  }

  fetchCrossSellForConfig(items, config) {
    const url = '/cross-sell-products';
    const body = {
      items: items,
      canvases: config.canvases || [],
      data_sources: config.data_sources || [],
      limit: config.limit || DEFAULT_LIMIT,
     disclaimer_mode: config.disclaimer_mode || DISCLAIMER_MODES.STANDARD,
    };

    Fetchers.fetchJSON(url, 'POST', JSON.stringify(body))
      .then(response => response.json())
      .then((data) => {
        if (data.status === 'ok') {
          this.fillCrossSellPlaceholder(data, config.moduleId);
        }
      })
      .catch((error) => {
        this.handleError(error);
      });
  }

  fetchCrossSell() {
    const items = this.getItemsInfo();

    // Don't make request if no items found
    if (!items || items.length === 0) {
      return;
    }

    // Prevent duplicate requests
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    const url = '/cross-sell-products';
    const body = {
      items: items,
      canvases: this.canvasesValue || [],
      data_sources: this.dataSourcesValue || [],
      limit: this.limitValue || DEFAULT_LIMIT,
      disclaimer_mode: this.disclaimerModeValue || DISCLAIMER_MODES.STANDARD,
    }

    Fetchers.fetchJSON(url, 'POST', JSON.stringify(body))
      .then(response => response.json())
      .then((data) => {
        if (data.status === 'ok') {
          this.renderCrossSell(data);
        }
      })
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  getItemsInfo() {
    const designData = [];

    document.querySelectorAll(this.targetedItemClassValue).forEach((element) => {
      designData.push({
        design_id: element.dataset.designId,
        canvas: element.dataset.canvas,
        primary_tag: element.dataset.primaryTag,
      });
    });

    return designData;
  }

  renderCrossSell(data) {
    if (!data.designs) return;
    this.element.innerHTML = data.core;
    this.updateMeta(data.meta || {});
    this.itemsContainerTarget.innerHTML = data.designs;
    this.pushCrossSellItemsToDataLayer(data.data_layer_hash);
  }

  // Container mode: fill the placeholder for a module with returned content
  fillCrossSellPlaceholder(data, moduleId) {
    if (!data.designs) return;

    const placeholder = this.modulePlaceholders && this.modulePlaceholders.get(moduleId);
    if (!placeholder) return;

    placeholder.innerHTML = data.core;

    // Populate meta elements within this placeholder
    this.updateMetaOnEl(placeholder, data.meta || {});

    // Populate items within this placeholder
    const itemsContainer = placeholder.querySelector('[data-cross-sell-target="itemsContainer"]');
    if (itemsContainer) itemsContainer.innerHTML = data.designs;

    this.pushCrossSellItemsToDataLayer(data.data_layer_hash);
  }

  updateMeta(meta = {}) {
    if (this.hasTitleTarget && meta.title) {
      this.titleTarget.textContent = meta.title;
    }

    if (this.hasDiscountMessageTarget) {
      if (meta.canvas_quantity_discount_message) {
        this.discountMessageTarget.innerHTML = meta.canvas_quantity_discount_message;
        this.discountMessageTarget.hidden = false;
      } else {
        this.discountMessageTarget.innerHTML = '';
        this.discountMessageTarget.hidden = true;
      }
    }

    if (this.hasDisclaimerTarget) {
      if (meta.disclaimer) {
        this.disclaimerTarget.innerHTML = meta.disclaimer;
        this.disclaimerTarget.hidden = false;
      } else {
        this.disclaimerTarget.innerHTML = '';
        this.disclaimerTarget.hidden = true;
      }
    }
  }

  // Update meta fields within a specific root element (container mode)
  updateMetaOnEl(rootEl, meta = {}) {
    const titleEl = rootEl.querySelector('[data-cross-sell-target="title"]');
    if (titleEl && meta.title) titleEl.textContent = meta.title;

    const discountEl = rootEl.querySelector('[data-cross-sell-target="discountMessage"]');
    if (discountEl) {
      if (meta.canvas_quantity_discount_message) {
        discountEl.innerHTML = meta.canvas_quantity_discount_message;
        discountEl.hidden = false;
      } else {
        discountEl.innerHTML = '';
        discountEl.hidden = true;
      }
    }

    const disclaimerEl = rootEl.querySelector('[data-cross-sell-target="disclaimer"]');
    if (disclaimerEl) {
      if (meta.disclaimer) {
        disclaimerEl.innerHTML = meta.disclaimer;
        disclaimerEl.hidden = false;
      } else {
        disclaimerEl.innerHTML = '';
        disclaimerEl.hidden = true;
      }
    }
  }

  pushCrossSellItemsToDataLayer(dataLayerHash) {
    try {
      dataLayer.push({
        'event': 'crossSellImpression',
        'cross_sell_items': dataLayerHash,
      });
    } catch (err) {
      console.error(err);
    }
  }

  generateModuleId() {
    return Math.random().toString(36).slice(2);
  }
}
